import React, { useState, useEffect } from "react";
import ContactVendorleadsModal from "../../components/ContactVendorleadsModal";
import SignInModal from "../../components/SignInModal";
import Navbar from "../../components/Navbar";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";
const BRAND_BLUE = "#3b82f6";
const GRAY_DARK_COLOR = "#374151";

/* ─────────── Demo agency / agent identity ─────────── */
const AI_AGENT = {
  full_name: "Michael Jones",
  first_name: "Michael",
  title: "Associate to John Doe",
  email: "michael.jones@demoagency.agency",
  agency: "Demo Agency",
  photo:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/profile-image.jpg",
  logo:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/logo_url_1774836185_14b54735.png",
};

/* ─────────── Keyframes ─────────── */
function GlobalAnimations() {
  return (
    <style>{`
      @keyframes fadeUp {
        0%   { opacity: 0; transform: translateY(10px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeIn {
        0%   { opacity: 0; }
        100% { opacity: 1; }
      }
      @keyframes slideInRight {
        0%   { opacity: 0; transform: translateX(24px); }
        100% { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideInLeft {
        0%   { opacity: 0; transform: translateX(-24px); }
        100% { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideInDown {
        0%   { opacity: 0; transform: translateY(-14px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes blink {
        0%, 49% { opacity: 1; }
        50%, 100% { opacity: 0; }
      }
      @keyframes brainPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.45), 0 0 30px rgba(16,185,129,0.35); }
        50%      { box-shadow: 0 0 0 18px rgba(16,185,129,0), 0 0 50px rgba(59,130,246,0.45); }
      }
      @keyframes orbit {
        0%   { transform: rotate(0deg) translateX(var(--r, 110px)) rotate(0deg); }
        100% { transform: rotate(360deg) translateX(var(--r, 110px)) rotate(-360deg); }
      }
      @keyframes dotFlow {
        0%   { transform: translateX(-100%); opacity: 0; }
        20%  { opacity: 1; }
        80%  { opacity: 1; }
        100% { transform: translateX(100%); opacity: 0; }
      }
      @keyframes barGrow {
        0%   { transform: scaleY(0.05); }
        100% { transform: scaleY(1); }
      }
      @keyframes tickPop {
        0%   { transform: scale(0); opacity: 0; }
        60%  { transform: scale(1.2); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes shimmer {
        0%   { background-position: -300px 0; }
        100% { background-position: 300px 0; }
      }
      @keyframes ringPulse {
        0%   { transform: scale(0.8); opacity: 0.55; }
        100% { transform: scale(2.2); opacity: 0; }
      }
      @keyframes floatY {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-6px); }
      }
      @keyframes wave {
        0%   { transform: scaleY(0.3); }
        50%  { transform: scaleY(1); }
        100% { transform: scaleY(0.3); }
      }
      @keyframes sweep {
        0%   { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      @keyframes drawLine {
        0%   { stroke-dashoffset: 200; }
        100% { stroke-dashoffset: 0; }
      }
      @keyframes badgeIn {
        0%   { opacity: 0; transform: translateY(6px) scale(0.9); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes statusBlink {
        0%, 100% { background: #10b981; box-shadow: 0 0 0 0 rgba(16,185,129,0.6); }
        50%      { background: #34d399; box-shadow: 0 0 0 6px rgba(16,185,129,0); }
      }
    `}</style>
  );
}

/* ─────────── Hero typing draft (email / SMS / call notes) ─────────── */
const HERO_EMAIL_LINES = [
  "Hi Sarah,",
  "",
  "Happy 2-year anniversary at 14 Maple Street, that went fast!",
  "",
  "I had a look at recent sales on your block this morning. A 3-bed on",
  "Bourke Street went for $1.72M last weekend (only 4 days on market),",
  "and the 2-bed terrace at #18 settled at $1.58M.",
  "",
  "Based on that, your place is tracking around 8.3% up since you bought.",
  "Happy to put a proper number on it whenever suits, no obligation.",
  "",
  "Cheers,",
  "Michael",
];

const HERO_SMS_LINES = [
  "Hey Sarah, Michael from Demo Agency 👋",
  "Quick one, a 3-bed on Bourke St just sold for $1.72M.",
  "Your 14 Maple is tracking ~8.3% up since you bought.",
  "Want a proper number? No obligation.",
];

const HERO_CALL_NOTES = {
  contact: "Sarah Miller",
  property: "14 Maple St, Surry Hills",
  when: "Briefing John for his 10:30 call",
  outcome: "Talk track ready",
  lines: [
    "Why you're calling",
    "Sarah hit her 2-year anniversary at 14 Maple and",
    "replied warmly to the January market update.",
    "She's owner-occupier, not actively selling.",
    "",
    "How to open",
    '"Hi Sarah, John from Demo Agency. Wanted to give',
    'you a quick anniversary call about 14 Maple..."',
    "",
    "Data points to drop in",
    "• 3-bed on Bourke St sold for $1.72M (4 days on market)",
    "• 14 Maple tracking ~8.3% up since she bought",
    "• Median for the block up 4.1% YoY",
    "",
    "If she asks about renos, mention spring guide.",
    "If she's busy, offer to SMS the comp instead.",
    "",
    "Goal of this call",
    "Book an in-home appraisal in spring.",
  ],
};

const HERO_TABS = [
  { key: "email", label: "Email",      icon: "✉️" },
  { key: "sms",   label: "SMS",        icon: "💬" },
  { key: "call",  label: "Call notes", icon: "📞" },
];

function useTypedText(fullText, format) {
  const [shown, setShown] = useState(0);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i += 2;
      if (i >= fullText.length + 40) {
        clearInterval(t);
        setTimeout(() => setCycle((c) => c + 1), 4000);
        return;
      }
      setShown(Math.min(i, fullText.length));
    }, 28);
    return () => clearInterval(t);
  }, [cycle, fullText.length]);

  useEffect(() => {
    setShown(0);
  }, [cycle, format]);

  return [fullText.slice(0, shown), () => setCycle((c) => c + 1)];
}

const Caret = () => (
  <span
    style={{
      display: "inline-block",
      width: 7,
      height: 14,
      background: BRAND_ACCENT_COLOR,
      verticalAlign: "middle",
      marginLeft: 2,
      animation: "blink 0.9s steps(2) infinite",
    }}
  />
);

/* ----- Email layout (Gmail-like) ----- */
function EmailDraftCard({ isMobile, visible }) {
  return (
    <>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <img
          src={AI_AGENT.photo}
          alt={AI_AGENT.full_name}
          style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", background: "#e5e7eb" }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
            {AI_AGENT.full_name}
            <span
              style={{
                fontSize: 9,
                background: "linear-gradient(90deg,#10b981,#3b82f6)",
                color: "#fff",
                fontWeight: 800,
                padding: "1px 6px",
                borderRadius: 4,
                letterSpacing: 0.5,
              }}
            >
              AI
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            {AI_AGENT.email} · to sarah.miller@example.com
          </div>
        </div>
      </div>
      <div style={{ padding: "10px 16px", borderBottom: "1px solid #f1f5f9", fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
        Sarah, quick note on 14 Maple St
      </div>
      <div
        style={{
          padding: "14px 16px 18px 16px",
          fontSize: isMobile ? 12 : 13,
          lineHeight: 1.65,
          color: "#0f172a",
          fontFamily: "ui-monospace, SFMono-Regular, monospace",
          whiteSpace: "pre-wrap",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {visible}
        <Caret />
      </div>
    </>
  );
}

/* ----- SMS layout (phone chat bubble) ----- */
function SmsDraftCard({ isMobile, visible }) {
  // Find which line is currently typing so earlier lines render as completed
  // bubbles and the in-progress one shows the caret.
  const allLines = HERO_SMS_LINES;
  const buffer = visible;
  const completed = [];
  let remaining = buffer;
  for (let i = 0; i < allLines.length; i++) {
    const line = allLines[i];
    if (remaining.length >= line.length + 1) {
      completed.push({ text: line, done: true });
      remaining = remaining.slice(line.length + 1);
    } else {
      completed.push({ text: remaining, done: false });
      break;
    }
  }
  return (
    <>
      {/* SMS contact header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#f59e0b,#ef4444)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: 12,
          }}
        >
          SM
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Sarah Miller</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>+61 412 555 218 · SMS</div>
        </div>
        <span
          style={{
            fontSize: 9,
            fontWeight: 800,
            color: "#475569",
            background: "#e2e8f0",
            padding: "2px 7px",
            borderRadius: 999,
            letterSpacing: 0.4,
          }}
        >
          160 CHARS
        </span>
      </div>

      {/* Bubble area */}
      <div
        style={{
          padding: "16px 14px",
          flex: 1,
          background: "#f1f5f9",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          overflow: "hidden",
        }}
      >
        {completed.map((b, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "flex-end" }}>
            <div
              style={{
                maxWidth: "82%",
                background: "linear-gradient(135deg,#10b981,#059669)",
                color: "#fff",
                fontSize: isMobile ? 13 : 13.5,
                lineHeight: 1.4,
                padding: "8px 12px",
                borderRadius: 14,
                borderBottomRightRadius: 4,
                fontFamily: "Inter, system-ui, sans-serif",
                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
              }}
            >
              {b.text}
              {!b.done && <Caret />}
            </div>
          </div>
        ))}
        <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "right", marginTop: 4 }}>
          Sent from Michael · AI · Demo Agency
        </div>
      </div>
    </>
  );
}

/* ----- (deprecated old CRM-log call notes; replaced by talk-track variant below) ----- */
function CallNotesCardOld_DEPRECATED_unused({ isMobile, visible }) { /* eslint-disable-line */
  // Kept temporarily as a placeholder to preserve diff context; not rendered.
  // Original body removed.
  return null;
}

// eslint-disable-next-line no-unused-vars
function _unused_legacy_call_notes_card({ isMobile, visible }) {
  return (
    <>
      {/* Call header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(59,130,246,0.12)",
            color: BRAND_BLUE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          📞
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
            Call with {HERO_CALL_NOTES.contact}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {HERO_CALL_NOTES.property} · {HERO_CALL_NOTES.duration} · {HERO_CALL_NOTES.when}
          </div>
        </div>
        <span
          style={{
            fontSize: 9,
            fontWeight: 800,
            background: "rgba(16,185,129,0.12)",
            color: BRAND_ACCENT_COLOR,
            padding: "2px 8px",
            borderRadius: 999,
            letterSpacing: 0.4,
            whiteSpace: "nowrap",
          }}
        >
          {HERO_CALL_NOTES.outcome.toUpperCase()}
        </span>
      </div>

      {/* Agent attribution row — who took the call */}
      <div
        style={{
          padding: "8px 16px",
          borderBottom: "1px solid #f1f5f9",
          background: "#f8fafc",
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 11,
          color: "#475569",
        }}
      >
        <img
          src={AI_AGENT.photo}
          alt=""
          style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }}
        />
        <span style={{ fontWeight: 700, color: "#0f172a" }}>{AI_AGENT.full_name}</span>
        <span
          style={{
            fontSize: 9,
            background: "linear-gradient(90deg,#10b981,#3b82f6)",
            color: "#fff",
            fontWeight: 800,
            padding: "1px 5px",
            borderRadius: 3,
            letterSpacing: 0.4,
          }}
        >
          AI
        </span>
        <span style={{ color: "#94a3b8" }}>logged the call · transcript attached</span>
      </div>

      {/* Notes body */}
      <div
        style={{
          padding: "14px 16px 18px 16px",
          fontSize: isMobile ? 12 : 13,
          lineHeight: 1.65,
          color: "#0f172a",
          whiteSpace: "pre-wrap",
          flex: 1,
          overflow: "hidden",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        {visible.split("\n").map((ln, i) => {
          const isHeading = ["Summary", "Key points", "Next step"].includes(ln);
          return (
            <div
              key={i}
              style={{
                fontSize: isHeading ? 10 : "inherit",
                fontWeight: isHeading ? 800 : 500,
                color: isHeading ? BRAND_ACCENT_COLOR : "#0f172a",
                letterSpacing: isHeading ? 0.8 : 0,
                textTransform: isHeading ? "uppercase" : "none",
                marginTop: isHeading && i !== 0 ? 8 : 0,
              }}
            >
              {ln || " "}
              {i === visible.split("\n").length - 1 && <Caret />}
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ----- Call notes layout (AI prepares a talk track for the HUMAN agent) ----- */
function CallNotesCard({ isMobile, visible }) {
  const HEADINGS = [
    "Why you're calling",
    "How to open",
    "Data points to drop in",
    "Goal of this call",
  ];
  const lines = visible.split("\n");
  return (
    <>
      {/* "From AI, for human agent" header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <img
          src={AI_AGENT.photo}
          alt=""
          style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            Talk track for John
            <span
              style={{
                fontSize: 9,
                background: "linear-gradient(90deg,#10b981,#3b82f6)",
                color: "#fff",
                fontWeight: 800,
                padding: "1px 5px",
                borderRadius: 3,
                letterSpacing: 0.4,
              }}
            >
              FROM AI
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Call: {HERO_CALL_NOTES.contact} · {HERO_CALL_NOTES.property}
          </div>
        </div>
        <span
          style={{
            fontSize: 9,
            fontWeight: 800,
            background: "rgba(245,158,11,0.15)",
            color: "#b45309",
            padding: "2px 8px",
            borderRadius: 999,
            letterSpacing: 0.4,
            whiteSpace: "nowrap",
          }}
        >
          BEFORE THE CALL
        </span>
      </div>

      {/* Context strip making it clear the HUMAN makes the call */}
      <div
        style={{
          padding: "8px 16px",
          borderBottom: "1px solid #f1f5f9",
          background: "#f8fafc",
          fontSize: 11,
          color: "#475569",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>🎯</span>
        <span>
          <strong style={{ color: "#0f172a" }}>You</strong> make the call. Michael just briefs you so you walk in knowing the relationship.
        </span>
      </div>

      {/* Talk track body */}
      <div
        style={{
          padding: "14px 16px 18px 16px",
          fontSize: isMobile ? 12 : 13,
          lineHeight: 1.6,
          color: "#0f172a",
          whiteSpace: "pre-wrap",
          flex: 1,
          overflow: "hidden",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        {lines.map((ln, i) => {
          const isHeading = HEADINGS.includes(ln);
          const isQuote = ln.startsWith('"');
          return (
            <div
              key={i}
              style={{
                fontSize: isHeading ? 10 : "inherit",
                fontWeight: isHeading ? 800 : 500,
                color: isHeading ? BRAND_ACCENT_COLOR : isQuote ? BRAND_BLUE : "#0f172a",
                letterSpacing: isHeading ? 0.8 : 0,
                textTransform: isHeading ? "uppercase" : "none",
                marginTop: isHeading && i !== 0 ? 8 : 0,
                fontStyle: isQuote ? "italic" : "normal",
              }}
            >
              {ln || " "}
              {i === lines.length - 1 && <Caret />}
            </div>
          );
        })}
      </div>
    </>
  );
}

function HeroEmailDraft({ isMobile }) {
  const [format, setFormat] = useState("email");
  const fullText =
    format === "email"
      ? HERO_EMAIL_LINES.join("\n")
      : format === "sms"
      ? HERO_SMS_LINES.join("\n")
      : HERO_CALL_NOTES.lines.join("\n");
  const [visible] = useTypedText(fullText, format);

  const handleFormat = (key) => {
    if (key === format) return;
    setFormat(key);
  };

  return (
    <div style={{ animation: "fadeUp 0.7s ease both" }}>
      {/* Format picker — pill toggle group above the card. Made obviously
          interactive: green active state + raised hover. */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: 4,
          marginBottom: 10,
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 999,
          backdropFilter: "blur(8px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: "rgba(255,255,255,0.55)",
            letterSpacing: 0.8,
            padding: "0 8px 0 10px",
            textTransform: "uppercase",
            borderRight: "1px solid rgba(255,255,255,0.12)",
            marginRight: 2,
          }}
        >
          Draft as
        </span>
        {HERO_TABS.map((t) => {
          const active = t.key === format;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => handleFormat(t.key)}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.12)";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 14px",
                fontSize: 12.5,
                fontWeight: 700,
                background: active ? BRAND_ACCENT_COLOR : "transparent",
                color: active ? "#fff" : "rgba(255,255,255,0.85)",
                border: "none",
                borderRadius: 999,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s",
                boxShadow: active ? "0 4px 12px rgba(16,185,129,0.45)" : "none",
              }}
            >
              <span style={{ fontSize: 14 }}>{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 30px 80px rgba(0,0,0,0.45), 0 4px 16px rgba(0,0,0,0.2)",
          border: "1px solid rgba(255,255,255,0.08)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Window toolbar */}
        <div
          style={{
            background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fca5a5" }} />
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fcd34d" }} />
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#86efac" }} />
          <div style={{ flex: 1, textAlign: "center", fontSize: 11, color: "#64748b", fontWeight: 600, letterSpacing: 0.4 }}>
            {format === "call" ? `Call log · ${AI_AGENT.first_name}` : `Drafting as ${AI_AGENT.first_name}`}
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "rgba(16,185,129,0.12)",
              color: BRAND_ACCENT_COLOR,
              fontSize: 10,
              fontWeight: 800,
              padding: "3px 8px",
              borderRadius: 999,
              letterSpacing: 0.4,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", animation: "statusBlink 1.6s ease infinite" }} />
            {format === "call" ? "AI LOGGING" : "AI WRITING"}
          </span>
        </div>

        {/* Body — fixed height so the card doesn't reflow as format changes */}
        <div
          style={{
            height: isMobile ? 380 : 400,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {format === "email" && <EmailDraftCard isMobile={isMobile} visible={visible} />}
          {format === "sms" && <SmsDraftCard isMobile={isMobile} visible={visible} />}
          {format === "call" && <CallNotesCard isMobile={isMobile} visible={visible} />}
        </div>

        {/* Footer ribbon — only meaningful for email/SMS (memory chips) */}
        {format !== "call" && (
          <div
            style={{
              background: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
              padding: "10px 14px",
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", letterSpacing: 0.5 }}>
              USING MEMORY:
            </span>
            {["Bought 14 Maple, Apr 2024", "Owner-occupier", "Replied warmly in Jan", "Opens every report"].map((m) => (
              <span
                key={m}
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  color: "#0f172a",
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "3px 7px",
                  borderRadius: 6,
                }}
              >
                {m}
              </span>
            ))}
          </div>
        )}
        {format === "call" && (
          <div
            style={{
              background: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
              padding: "10px 14px",
              fontSize: 10.5,
              color: "#475569",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: BRAND_ACCENT_COLOR, fontWeight: 800, letterSpacing: 0.5 }}>
              SAVED TO CRM
            </span>
            · contact card updated · 2 follow-ups scheduled
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────── Anatomy of an AI Agent ─────────── */
// Layout: a clean two-column grid. Left column is the agent profile card
// (large, prominent). Right column is a stacked list of the six attributes
// that make it a teammate rather than a chatbot. Predictable, no floating
// absolute labels, no SVG lines.
function AgentAnatomy({ isMobile }) {
  const attributes = [
    { icon: "🖼️", title: "Profile photo",       desc: "A real face on every reply, not a robot avatar." },
    { icon: "📛", title: "Full + first name",   desc: "Greets contacts personally, by name." },
    { icon: "💼", title: "Job title",           desc: '"Associate to John Doe", sits inside your team.' },
    { icon: "✉️", title: "Custom-domain inbox", desc: "you@youragency.agency, no Gmail addresses." },
    { icon: "✍️", title: "Inline signature",    desc: "Photo, role, and contact details on every send." },
    { icon: "🧠", title: "Persona memory",      desc: "Briefed on every contact's full history." },
  ];

  return (
    <div
      style={{
        background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 60%,#134e4a 100%)",
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
        padding: isMobile ? "1.5rem 1.25rem" : "2.5rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle grid backdrop */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "0.85fr 1.15fr",
          gap: isMobile ? 24 : 40,
          alignItems: "center",
        }}
      >
        {/* Left: agent profile card */}
        <div
          style={{
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 16,
            padding: isMobile ? 20 : 28,
            textAlign: "center",
            animation: "fadeUp 0.6s ease both",
          }}
        >
          <div style={{ position: "relative", display: "inline-block", marginBottom: 14 }}>
            <span
              style={{
                position: "absolute",
                inset: -10,
                borderRadius: "50%",
                animation: "ringPulse 2s ease-out infinite",
                background: "rgba(16,185,129,0.5)",
              }}
            />
            <img
              src={AI_AGENT.photo}
              alt={AI_AGENT.full_name}
              style={{
                width: isMobile ? 96 : 112,
                height: isMobile ? 96 : 112,
                borderRadius: "50%",
                objectFit: "cover",
                border: "3px solid rgba(255,255,255,0.25)",
                position: "relative",
                zIndex: 1,
              }}
            />
            <span
              style={{
                position: "absolute",
                right: -4,
                bottom: 2,
                background: "linear-gradient(135deg,#10b981,#3b82f6)",
                color: "#fff",
                fontSize: 10,
                fontWeight: 800,
                padding: "3px 8px",
                borderRadius: 999,
                border: "2px solid #0f172a",
                zIndex: 2,
                letterSpacing: 0.5,
              }}
            >
              AI
            </span>
          </div>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: isMobile ? 18 : 22 }}>{AI_AGENT.full_name}</div>
          <div style={{ color: BRAND_ACCENT_COLOR, fontWeight: 700, fontSize: 12, marginTop: 4, letterSpacing: 0.3 }}>
            {AI_AGENT.title}
          </div>
          <div
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: 11.5,
              marginTop: 10,
              padding: "5px 10px",
              background: "rgba(255,255,255,0.04)",
              borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.08)",
              display: "inline-block",
              wordBreak: "break-all",
            }}
          >
            {AI_AGENT.email}
          </div>
          <div
            style={{
              marginTop: 14,
              padding: "5px 12px",
              background: "rgba(16,185,129,0.15)",
              border: "1px solid rgba(16,185,129,0.3)",
              borderRadius: 999,
              fontSize: 10.5,
              color: BRAND_ACCENT_COLOR,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: "50%", animation: "statusBlink 1.6s ease infinite" }} />
            ONLINE · NURTURING 1,284 CONTACTS
          </div>
        </div>

        {/* Right: attribute list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {attributes.map((a, i) => (
            <div
              key={a.title}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "12px 14px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 12,
                animation: `slideInRight 0.5s ease ${0.1 + i * 0.07}s both`,
              }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "rgba(16,185,129,0.12)",
                  border: "1px solid rgba(16,185,129,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                {a.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: BRAND_ACCENT_COLOR, fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase" }}>
                  {a.title}
                </div>
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13.5, marginTop: 2, lineHeight: 1.45 }}>
                  {a.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Step-by-step Browser walkthrough ─────────── */
const WALK_STEPS = [
  { kicker: "Step 1", title: "Pick a persona", blurb: "Photo, name, title, your AI's identity in 60 seconds." },
  { kicker: "Step 2", title: "Plug in inbox history", blurb: "One Gmail/Outlook export gives your AI years of memory." },
  { kicker: "Step 3", title: "Assign ownership", blurb: '"AI owns" the long tail. "I own" the hot ones.' },
  { kicker: "Step 4", title: "Brief the AI", blurb: "Global instructions plus per-contact memory." },
  { kicker: "Step 5", title: "Watch replies roll in", blurb: "Warmed-up contacts auto-handed back to you." },
];

function BrowserFrame({ children, url = "app.vendorleads.com.au/ai-agent" }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
        width: "100%",
        minWidth: 0,
      }}
    >
      <div
        style={{
          background: "#f1f5f9",
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: 6,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fca5a5", flexShrink: 0 }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fcd34d", flexShrink: 0 }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#86efac", flexShrink: 0 }} />
        <div
          style={{
            marginLeft: 10,
            flex: 1,
            minWidth: 0,
            background: "#fff",
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 10.5,
            color: "#64748b",
            border: "1px solid #e2e8f0",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {url}
        </div>
      </div>
      <div style={{ padding: 16, height: 400, overflow: "hidden" }}>{children}</div>
    </div>
  );
}

function StepPickPersona() {
  const [titleText, setTitleText] = useState("");
  const fullTitle = AI_AGENT.title;
  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % (fullTitle.length + 20);
      setTitleText(fullTitle.slice(0, Math.min(i, fullTitle.length)));
    }, 80);
    return () => clearInterval(t);
  }, [fullTitle]);

  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>
        Build your AI agent
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ position: "relative" }}>
            <img
              src={AI_AGENT.photo}
              alt=""
              style={{
                width: 88,
                height: 88,
                borderRadius: "50%",
                objectFit: "cover",
                border: "3px solid #fff",
                boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -4,
                right: -4,
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: BRAND_BLUE,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                border: "2px solid #fff",
              }}
            >
              ✎
            </div>
          </div>
          <div style={{ fontSize: 10, color: "#64748b", marginTop: 6, fontWeight: 600 }}>Upload photo</div>
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            ["Full name", AI_AGENT.full_name, false],
            ["First name", AI_AGENT.first_name, false],
            ["Title", titleText, true],
          ].map(([label, val, focused], i) => (
            <div key={i}>
              <div style={{ fontSize: 9.5, color: "#6b7280", fontWeight: 600, marginBottom: 2, letterSpacing: 0.3 }}>
                {label.toUpperCase()}
              </div>
              <div
                style={{
                  background: "#fff",
                  border: `1px solid ${focused ? BRAND_BLUE : "#e5e7eb"}`,
                  borderRadius: 6,
                  padding: "7px 10px",
                  fontSize: 12,
                  color: "#0f172a",
                  fontWeight: 600,
                  boxShadow: focused ? `0 0 0 3px ${BRAND_BLUE}26` : "none",
                }}
              >
                {val}
                {focused && (
                  <span
                    style={{
                      display: "inline-block",
                      width: 1.5,
                      height: 13,
                      background: "#0f172a",
                      verticalAlign: "middle",
                      marginLeft: 1,
                      animation: "blink 0.9s steps(2) infinite",
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          marginTop: 14,
          padding: "8px 12px",
          background: "rgba(16,185,129,0.1)",
          border: "1px solid rgba(16,185,129,0.25)",
          borderRadius: 8,
          fontSize: 11,
          color: "#047857",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span>✨</span> Persona saved, every email will be sent as {AI_AGENT.first_name}.
      </div>
    </div>
  );
}

function StepInboxImport() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setProgress((p) => (p >= 100 ? 100 : p + 3));
    }, 70);
    return () => clearInterval(t);
  }, []);
  const done = progress >= 100;
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>
        Import inbox history
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
        {[
          ["Gmail", "📧", true],
          ["Outlook", "📨", false],
          ["Other", "✉️", false],
        ].map(([n, e, on]) => (
          <div
            key={n}
            style={{
              border: `1.5px solid ${on ? BRAND_BLUE : "#e5e7eb"}`,
              background: on ? "#eff6ff" : "#fff",
              borderRadius: 8,
              padding: "10px 8px",
              textAlign: "center",
              fontSize: 11.5,
              fontWeight: 700,
              color: on ? BRAND_BLUE : "#374151",
            }}
          >
            <div style={{ fontSize: 18, marginBottom: 4 }}>{e}</div>
            {n}
          </div>
        ))}
      </div>
      <div
        style={{
          border: `2px dashed ${done ? BRAND_ACCENT_COLOR : BRAND_BLUE}`,
          borderRadius: 10,
          padding: "16px 12px",
          textAlign: "center",
          background: done ? "rgba(16,185,129,0.06)" : "rgba(59,130,246,0.05)",
          transition: "all 0.3s",
        }}
      >
        <div style={{ fontSize: 22, marginBottom: 4 }}>{done ? "✅" : "📦"}</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
          {done ? "takeout-export.zip" : "Drag your Google Takeout .zip here"}
        </div>
        <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 2 }}>
          {done ? "4,812 emails read · 1,284 contacts briefed" : "No need to unzip"}
        </div>
        <div
          style={{
            marginTop: 10,
            height: 6,
            background: "#e5e7eb",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: `linear-gradient(90deg,${BRAND_BLUE},${BRAND_ACCENT_COLOR})`,
              transition: "width 0.07s linear",
            }}
          />
        </div>
      </div>
      {done && (
        <div
          style={{
            marginTop: 10,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 11,
            color: "#0f172a",
            animation: "slideInDown 0.4s ease both",
          }}
        >
          <span style={{ color: BRAND_ACCENT_COLOR, fontWeight: 800 }}>Relationship summary built:</span>{" "}
          "Bought in Apr 2024, owner-occupier, replied warmly in Jan, opens every report."
        </div>
      )}
    </div>
  );
}

function StepOwnership() {
  const contacts = [
    { name: "Sarah Miller", owner: "ai", hot: false },
    { name: "David Chen", owner: "ai", hot: false },
    { name: "Priya Patel", owner: "me", hot: true },
    { name: "Tom Reilly", owner: "ai", hot: false },
    { name: "Anna Petrov", owner: "ai", hot: false },
    { name: "Marcus Hall", owner: "me", hot: true },
  ];
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>
        Assign contact ownership
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {contacts.map((c, i) => (
          <div
            key={c.name}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 10px",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              animation: `slideInRight 0.4s ease ${i * 0.06}s both`,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: `linear-gradient(135deg, hsl(${i * 60}, 60%, 60%), hsl(${i * 60 + 40}, 60%, 50%))`,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {c.name.charAt(0)}
            </div>
            <div style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
              {c.name}
              {c.hot && (
                <span
                  style={{
                    marginLeft: 6,
                    background: "rgba(239,68,68,0.12)",
                    color: "#dc2626",
                    fontSize: 9,
                    fontWeight: 800,
                    padding: "1px 6px",
                    borderRadius: 999,
                    letterSpacing: 0.3,
                  }}
                >
                  🔥 HOT
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                padding: "3px 9px",
                borderRadius: 999,
                background: c.owner === "ai" ? "rgba(16,185,129,0.15)" : "rgba(59,130,246,0.15)",
                color: c.owner === "ai" ? BRAND_ACCENT_COLOR : BRAND_BLUE,
                letterSpacing: 0.4,
              }}
            >
              {c.owner === "ai" ? "🤖 AI OWNS" : "👤 I OWN"}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 10,
          padding: "8px 12px",
          background: "rgba(16,185,129,0.08)",
          borderRadius: 8,
          fontSize: 11,
          color: "#047857",
          fontWeight: 600,
        }}
      >
        ✓ AI nurturing 4 long-tail contacts · Hands back to you when warm
      </div>
    </div>
  );
}

function StepBriefAI() {
  const lines = [
    "Always be friendly, concise, and helpful.",
    "Mention we offer free property appraisals when relevant.",
    "Never quote a sale price without referring to recent comps.",
    "Sign off as Michael, Associate to John Doe.",
  ];
  const [count, setCount] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCount((c) => (c >= lines.length ? lines.length : c + 1)), 700);
    return () => clearInterval(t);
  }, [lines.length]);
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
        Global AI instructions
      </div>
      <div
        style={{
          background: "#0f172a",
          borderRadius: 10,
          padding: 14,
          fontFamily: "ui-monospace, SFMono-Regular, monospace",
          fontSize: 12,
          color: "#e2e8f0",
          minHeight: 180,
        }}
      >
        {lines.slice(0, count).map((l, i) => (
          <div
            key={i}
            style={{
              padding: "4px 0",
              display: "flex",
              gap: 8,
              animation: "fadeUp 0.3s ease both",
            }}
          >
            <span style={{ color: "#64748b" }}>{i + 1}.</span>
            <span>{l}</span>
          </div>
        ))}
        {count < lines.length && (
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 14,
              background: BRAND_ACCENT_COLOR,
              verticalAlign: "middle",
              animation: "blink 0.9s steps(2) infinite",
            }}
          />
        )}
      </div>
      <div
        style={{
          marginTop: 10,
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        {["{{ai_agent_name}}", "{{ai_title}}", "{{agency_name}}", "{{contact_first_name}}"].map((v) => (
          <span
            key={v}
            style={{
              background: "rgba(59,130,246,0.1)",
              color: BRAND_BLUE,
              fontSize: 10,
              fontWeight: 700,
              padding: "3px 7px",
              borderRadius: 5,
              border: "1px solid rgba(59,130,246,0.2)",
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
            }}
          >
            {v}
          </span>
        ))}
      </div>
    </div>
  );
}

function StepReplies() {
  const events = [
    { who: "Sarah M.", what: "replied, wants an appraisal", time: "2m", warm: true },
    { who: "David C.", what: "opened report (3rd time)", time: "8m", warm: false },
    { who: "Priya P.", what: "asked about Bondi sales", time: "14m", warm: true },
    { who: "Tom R.", what: "clicked anniversary email", time: "27m", warm: false },
    { who: "Anna P.", what: "replied, thanked Michael", time: "41m", warm: false },
  ];
  const [shown, setShown] = useState(1);
  useEffect(() => {
    const t = setInterval(() => setShown((s) => (s >= events.length ? events.length : s + 1)), 900);
    return () => clearInterval(t);
  }, [events.length]);
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
        Live activity
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "rgba(16,185,129,0.12)",
            color: BRAND_ACCENT_COLOR,
            fontSize: 9,
            fontWeight: 800,
            padding: "2px 7px",
            borderRadius: 999,
            letterSpacing: 0.4,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: "50%", animation: "statusBlink 1.6s ease infinite" }} />
          LIVE
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {events.slice(0, shown).map((e, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 10px",
              background: e.warm ? "rgba(239,68,68,0.05)" : "#fff",
              border: `1px solid ${e.warm ? "rgba(239,68,68,0.25)" : "#e5e7eb"}`,
              borderRadius: 8,
              animation: "slideInDown 0.45s ease both",
            }}
          >
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: e.warm ? "rgba(239,68,68,0.15)" : "rgba(59,130,246,0.12)",
                color: e.warm ? "#dc2626" : BRAND_BLUE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                flexShrink: 0,
              }}
            >
              {e.warm ? "🔥" : "✉️"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{e.who}</div>
              <div style={{ fontSize: 11, color: "#64748b" }}>{e.what}</div>
            </div>
            {e.warm && (
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  padding: "3px 7px",
                  borderRadius: 999,
                  background: "#dc2626",
                  color: "#fff",
                  letterSpacing: 0.4,
                }}
              >
                HANDED BACK
              </div>
            )}
            <div style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0 }}>{e.time}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WalkthroughStepper({ isMobile }) {
  const [step, setStep] = useState(0);
  const [token, setToken] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % WALK_STEPS.length), 4200);
    return () => clearInterval(t);
  }, [token]);
  const Body = [StepPickPersona, StepInboxImport, StepOwnership, StepBriefAI, StepReplies][step];
  const urls = [
    "app.vendorleads.com.au/ai-agent/persona",
    "app.vendorleads.com.au/ai-agent/import",
    "app.vendorleads.com.au/contacts",
    "app.vendorleads.com.au/ai-agent/instructions",
    "app.vendorleads.com.au/inbox",
  ];

  return (
    <div
      style={{
        background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)",
        borderRadius: 18,
        padding: isMobile ? "1.5rem 1.25rem" : "2rem",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1.2fr",
          gap: isMobile ? 20 : 32,
          position: "relative",
          zIndex: 1,
          alignItems: "center",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.5,
              marginBottom: 14,
              textTransform: "uppercase",
            }}
          >
            From hire to first reply · in a single afternoon
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {WALK_STEPS.map((s, i) => {
              const active = i === step;
              const done = i < step;
              return (
                <button
                  key={s.title}
                  type="button"
                  onClick={() => {
                    setStep(i);
                    setToken((c) => c + 1);
                  }}
                  style={{
                    display: "flex",
                    gap: 12,
                    borderRadius: 12,
                    padding: isMobile ? "10px 12px" : "12px 14px",
                    background: active ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${active ? "rgba(16,185,129,0.45)" : "rgba(255,255,255,0.08)"}`,
                    transition: "all 0.4s ease",
                    opacity: done ? 0.65 : 1,
                    cursor: "pointer",
                    textAlign: "left",
                    font: "inherit",
                    color: "inherit",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: active ? BRAND_ACCENT_COLOR : done ? "rgba(16,185,129,0.55)" : "rgba(255,255,255,0.1)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 800,
                      flexShrink: 0,
                      boxShadow: active ? "0 0 0 4px rgba(16,185,129,0.25)" : "none",
                    }}
                  >
                    {done ? "✓" : i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: 1.2,
                        color: active ? BRAND_ACCENT_COLOR : "rgba(255,255,255,0.55)",
                        textTransform: "uppercase",
                      }}
                    >
                      {s.kicker}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", margin: "2px 0 3px 0" }}>
                      {s.title}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
                      {s.blurb}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ minWidth: 0 }} key={`browser-${step}`}>
          <BrowserFrame url={urls[step]}>
            <Body />
          </BrowserFrame>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Memory section ─────────── */
function MemoryDemo({ isMobile }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStage((s) => (s + 1) % 3), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr auto 1fr",
        gap: 20,
        alignItems: "center",
      }}
    >
      {/* Raw inbox */}
      <div
        style={{
          background: "#0f172a",
          borderRadius: 12,
          padding: 16,
          border: "1px solid rgba(255,255,255,0.08)",
          minHeight: 280,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: 1, marginBottom: 10 }}>
          RAW INBOX · 4,812 EMAILS
        </div>
        {[
          { d: "Apr '24", s: "Re: Inspection at 14 Maple", b: "Hi Michael, we're definitely interested..." },
          { d: "Jun '24", s: "Settlement confirmed 🎉", b: "Thanks so much for everything, you've made..." },
          { d: "Jan '25", s: "Re: Quick market update", b: "Wow that's a great update. We love the place..." },
          { d: "Mar '25", s: "Just curious about prices", b: "Hi Michael, no plans yet but interested to..." },
          { d: "Today", s: "(new)", b: "..." },
        ].map((e, i) => (
          <div
            key={i}
            style={{
              padding: "7px 0",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              opacity: i < 4 ? 1 : 0.3,
              animation: `slideInLeft 0.4s ease ${i * 0.08}s both`,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div style={{ color: "#e2e8f0", fontSize: 11, fontWeight: 700 }}>{e.s}</div>
              <div style={{ color: "#64748b", fontSize: 10 }}>{e.d}</div>
            </div>
            <div style={{ color: "#64748b", fontSize: 10.5, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {e.b}
            </div>
          </div>
        ))}
      </div>

      {/* Arrow / brain */}
      <div style={{ display: "flex", flexDirection: isMobile ? "row" : "column", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#10b981,#3b82f6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
            animation: "brainPulse 2s ease-in-out infinite",
          }}
        >
          🧠
        </div>
        <div style={{ position: "relative", width: isMobile ? 80 : 4, height: isMobile ? 4 : 80, background: "rgba(16,185,129,0.2)", borderRadius: 999, overflow: "hidden" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(${isMobile ? "90deg" : "180deg"}, transparent, ${BRAND_ACCENT_COLOR}, transparent)`,
              animation: "dotFlow 1.8s linear infinite",
            }}
          />
        </div>
        <div
          style={{
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: 1,
            color: BRAND_ACCENT_COLOR,
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          Distill
        </div>
      </div>

      {/* Summary card */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 18,
          border: `2px solid ${BRAND_ACCENT_COLOR}`,
          boxShadow: "0 20px 50px rgba(16,185,129,0.18)",
          minHeight: 280,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "linear-gradient(135deg,#f59e0b,#ef4444)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 15,
            }}
          >
            SM
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Sarah Miller</div>
            <div style={{ fontSize: 11, color: "#64748b" }}>14 Maple St · Owner-occupier</div>
          </div>
        </div>
        <div
          style={{
            fontSize: 9,
            fontWeight: 800,
            color: BRAND_ACCENT_COLOR,
            letterSpacing: 1,
            marginBottom: 6,
          }}
        >
          RELATIONSHIP SUMMARY
        </div>
        <div style={{ fontSize: 12.5, color: "#0f172a", lineHeight: 1.6 }}>
          {stage >= 0 && (
            <span style={{ animation: "fadeIn 0.4s ease both" }}>
              Bought <strong>14 Maple Street, Surry Hills</strong> in Apr 2024 for $1.45M.
            </span>
          )}{" "}
          {stage >= 1 && (
            <span style={{ animation: "fadeIn 0.4s ease both" }}>
              Replied warmly to January market update, expressed interest in long-term value.
            </span>
          )}{" "}
          {stage >= 2 && (
            <span style={{ animation: "fadeIn 0.4s ease both" }}>
              Asked about local prices in March (passive curiosity, not a seller yet).{" "}
              <strong style={{ color: BRAND_ACCENT_COLOR }}>Nurture quarterly.</strong>
            </span>
          )}
          <span
            style={{
              display: "inline-block",
              width: 6,
              height: 13,
              background: BRAND_ACCENT_COLOR,
              verticalAlign: "middle",
              marginLeft: 2,
              animation: "blink 0.9s steps(2) infinite",
            }}
          />
        </div>
        <div
          style={{
            marginTop: 14,
            padding: "8px 10px",
            background: "rgba(16,185,129,0.08)",
            borderRadius: 8,
            fontSize: 11,
            color: "#047857",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>🔒</span> Raw emails stay private, only the summary briefs the AI.
        </div>
      </div>
    </div>
  );
}

/* ─────────── Chat with your AI teammate ─────────── */
const CHAT_MESSAGES = [
  { from: "you", text: "Write me an article about the Surry Hills market this quarter." },
  { from: "ai", text: "On it. Drafting now, should I keep it punchy (~400 words) or long-form?" },
  { from: "you", text: "Punchy. And pull a chart of median prices." },
  { from: "ai", text: "Done. Article + chart ready. Want me to schedule it for LinkedIn?" },
];

function TeammateChat({ isMobile }) {
  const messages = CHAT_MESSAGES;
  const [count, setCount] = useState(0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (count >= messages.length) {
      const reset = setTimeout(() => setCount(0), 4500);
      return () => clearTimeout(reset);
    }
    const isAi = messages[count].from === "ai";
    if (isAi) {
      setTyping(true);
      const t = setTimeout(() => {
        setTyping(false);
        setCount((c) => c + 1);
      }, 1400);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setCount((c) => c + 1), 1100);
      return () => clearTimeout(t);
    }
  }, [count, messages]);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        boxShadow: "0 30px 60px rgba(15,23,42,0.12)",
        overflow: "hidden",
        maxWidth: 520,
        margin: "0 auto",
        width: "100%",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "#f8fafc",
        }}
      >
        <img src={AI_AGENT.photo} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{AI_AGENT.full_name}</div>
          <div style={{ fontSize: 11, color: BRAND_ACCENT_COLOR, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", animation: "statusBlink 1.6s ease infinite" }} />
            Active
          </div>
        </div>
        <span style={{ fontSize: 9, fontWeight: 800, background: "linear-gradient(90deg,#10b981,#3b82f6)", color: "#fff", padding: "3px 8px", borderRadius: 999, letterSpacing: 0.5 }}>
          AI
        </span>
      </div>

      <div style={{ padding: 16, minHeight: 340, display: "flex", flexDirection: "column", gap: 10, background: "#fafbfc" }}>
        {messages.slice(0, count).map((m, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: m.from === "you" ? "flex-end" : "flex-start",
              animation: "fadeUp 0.35s ease both",
            }}
          >
            <div
              style={{
                maxWidth: "78%",
                padding: "9px 13px",
                borderRadius: 14,
                background: m.from === "you" ? BRAND_BLUE : "#fff",
                color: m.from === "you" ? "#fff" : "#0f172a",
                fontSize: 13,
                lineHeight: 1.45,
                border: m.from === "you" ? "none" : "1px solid #e2e8f0",
                boxShadow: m.from === "you" ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
                borderBottomRightRadius: m.from === "you" ? 4 : 14,
                borderBottomLeftRadius: m.from === "ai" ? 4 : 14,
              }}
            >
              {m.text}
            </div>
          </div>
        ))}
        {typing && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div
              style={{
                padding: "10px 14px",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                borderBottomLeftRadius: 4,
                display: "flex",
                gap: 4,
              }}
            >
              {[0, 1, 2].map((d) => (
                <span
                  key={d}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#94a3b8",
                    animation: `floatY 1s ease-in-out ${d * 0.15}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: 12, borderTop: "1px solid #f1f5f9", display: "flex", gap: 8 }}>
        <div
          style={{
            flex: 1,
            background: "#f1f5f9",
            borderRadius: 999,
            padding: "9px 14px",
            fontSize: 12.5,
            color: "#94a3b8",
          }}
        >
          Ask {AI_AGENT.first_name} anything…
        </div>
        <button
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "none",
            background: BRAND_BLUE,
            color: "#fff",
            fontSize: 14,
            cursor: "default",
          }}
        >
          ➤
        </button>
      </div>
    </div>
  );
}

/* ─────────── Smart triggers (monitoring grid) ─────────── */
// New approach: a clean 2x4 grid of trigger "watchers". Each card shows the
// trigger, what it's watching, a live count, and a fired-just-now state that
// cycles across the grid so the section feels alive without floating chaos.
const TRIGGERS = [
  { icon: "🏠", label: "Purchase anniversary", watching: "Owners hitting 1, 2, or 5 years",   count: 12 },
  { icon: "🎂", label: "Birthday",              watching: "Contacts with a birthday today",    count: 3  },
  { icon: "📊", label: "First report viewed",   watching: "New users who opened a report",     count: 8  },
  { icon: "📧", label: "Repeated opens",        watching: "Contacts opening 3+ times this week", count: 5 },
  { icon: "🔑", label: "First login",           watching: "Fresh signups in the last 24h",     count: 2  },
  { icon: "💬", label: "Reply detected",        watching: "Inbound emails or SMS in the last hour", count: 1 },
  { icon: "📅", label: "Quiet for 90 days",     watching: "Contacts who've gone silent",       count: 47 },
  { icon: "⚙️", label: "Custom event",          watching: "Anything you push via the API",     count: null },
];

function TriggerRadial({ isMobile }) {
  // Cycle a "FIRED" pulse across one card at a time so the grid feels live.
  const [pulseIdx, setPulseIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPulseIdx((i) => (i + 1) % TRIGGERS.length), 1500);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      style={{
        background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 60%,#134e4a 100%)",
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
        padding: isMobile ? "1.25rem" : "1.75rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Backdrop grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          pointerEvents: "none",
        }}
      />

      {/* Header strip */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "linear-gradient(135deg,#10b981,#3b82f6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              animation: "brainPulse 2.4s ease-in-out infinite",
            }}
          >
            🤖
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 13 }}>Trigger watchers</div>
            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11 }}>8 active · scanning your database</div>
          </div>
        </div>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 10,
            fontWeight: 800,
            color: BRAND_ACCENT_COLOR,
            background: "rgba(16,185,129,0.12)",
            border: "1px solid rgba(16,185,129,0.3)",
            padding: "3px 9px",
            borderRadius: 999,
            letterSpacing: 0.5,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: "50%", animation: "statusBlink 1.6s ease infinite" }} />
          LIVE
        </span>
      </div>

      {/* Trigger grid */}
      <div
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 10,
        }}
      >
        {TRIGGERS.map((t, i) => {
          const fired = i === pulseIdx;
          return (
            <div
              key={t.label}
              style={{
                background: fired ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.04)",
                border: `1px solid ${fired ? "rgba(16,185,129,0.45)" : "rgba(255,255,255,0.08)"}`,
                borderRadius: 12,
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                transition: "all 0.35s",
                boxShadow: fired ? "0 0 0 4px rgba(16,185,129,0.08)" : "none",
                animation: `slideInDown 0.45s ease ${i * 0.05}s both`,
              }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "rgba(16,185,129,0.15)",
                  border: "1px solid rgba(16,185,129,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                {t.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between" }}>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{t.label}</span>
                  {fired ? (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 800,
                        padding: "2px 6px",
                        borderRadius: 999,
                        background: BRAND_ACCENT_COLOR,
                        color: "#fff",
                        letterSpacing: 0.5,
                        animation: "fadeIn 0.3s ease both",
                      }}
                    >
                      FIRED
                    </span>
                  ) : (
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontWeight: 600 }}>
                      {t.count === null ? "API" : `${t.count} due`}
                    </span>
                  )}
                </div>
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t.watching}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: shows the result of a fire */}
      <div
        style={{
          position: "relative",
          marginTop: 14,
          padding: "10px 14px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 12,
          color: "rgba(255,255,255,0.8)",
        }}
      >
        <span style={{ fontSize: 16 }}>✉️</span>
        <span>
          When a watcher fires, Michael drafts a personal email, SMS, or call-prep note for that contact, ready in seconds.
        </span>
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function _unused_legacy_trigger_radial({ isMobile }) {
  return (
    <div
      style={{
        position: "relative",
        height: isMobile ? 380 : 440,
        background: "linear-gradient(135deg,#0f172a,#134e4a)",
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at center, rgba(16,185,129,0.12), transparent 60%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Center brain */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: 96,
          height: 96,
          borderRadius: "50%",
          background: "linear-gradient(135deg,#10b981,#3b82f6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 36,
          animation: "brainPulse 2.4s ease-in-out infinite",
          zIndex: 3,
        }}
      >
        🤖
      </div>

      {/* Orbits — radii match the badge placement so badges sit ON the rings */}
      {(isMobile ? [100, 150] : [120, 180]).map((r, ringIdx) => (
        <div
          key={r}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: r * 2,
            height: r * 2,
            marginTop: -r,
            marginLeft: -r,
            borderRadius: "50%",
            border: "1px dashed rgba(16,185,129,0.2)",
            zIndex: 1,
          }}
        />
      ))}

      {/* Trigger badges. Outer wrapper handles positioning; inner div owns the
          float/fade-in animations so their transform doesn't clobber the
          orbital offset. */}
      {TRIGGERS.map((t, i) => {
        const angle = (i / TRIGGERS.length) * Math.PI * 2 - Math.PI / 2;
        // Sit badges ALTERNATELY on the inner and outer orbit so they actually
        // land on the dashed rings (instead of floating between them).
        const onOuter = i % 2 === 0;
        const r = onOuter ? (isMobile ? 150 : 180) : (isMobile ? 100 : 120);
        const x = Math.cos(angle) * r;
        const y = Math.sin(angle) * r;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: `translate(${x}px, ${y}px)`,
              zIndex: 2,
            }}
          >
            <div
              style={{
                transform: "translate(-50%, -50%)",
                animation: `badgeIn 0.5s ease ${i * 0.08}s both`,
              }}
            >
              <div
                style={{
                  background: "rgba(15,23,42,0.85)",
                  backdropFilter: "blur(6px)",
                  border: "1px solid rgba(16,185,129,0.3)",
                  borderRadius: 999,
                  padding: "6px 12px 6px 6px",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                  animation: `floatY 3s ease-in-out ${i * 0.2}s infinite`,
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "rgba(16,185,129,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                  }}
                >
                  {t.icon}
                </span>
                <span style={{ color: "#fff", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{t.label}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────── Funnel result bars ─────────── */
function ResultsChart({ isMobile }) {
  const data = [
    { label: "Contacts in DB", n: 1284, color: "#64748b" },
    { label: "AI nurturing", n: 1142, color: BRAND_BLUE },
    { label: "Engaged this month", n: 387, color: "#8b5cf6" },
    { label: "Replied to AI", n: 94, color: BRAND_ACCENT_COLOR },
    { label: "Handed back warm", n: 23, color: "#f59e0b" },
    { label: "Appraisals booked", n: 7, color: "#ef4444" },
  ];
  const max = data[0].n;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {data.map((d, i) => (
        <div key={d.label} style={{ animation: `fadeUp 0.5s ease ${i * 0.08}s both` }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: GRAY_DARK_COLOR, marginBottom: 4 }}>
            <span>{d.label}</span>
            <span style={{ color: d.color }}>{d.n.toLocaleString()}</span>
          </div>
          <div
            style={{
              height: 14,
              borderRadius: 999,
              background: "#f1f5f9",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                width: `${(d.n / max) * 100}%`,
                background: `linear-gradient(90deg, ${d.color}, ${d.color}cc)`,
                borderRadius: 999,
                transformOrigin: "left center",
                animation: `barGrow 1s cubic-bezier(0.2,0.9,0.3,1.1) ${0.2 + i * 0.1}s both`,
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                animation: `sweep 2.4s linear ${i * 0.3}s infinite`,
                width: 80,
              }}
            />
          </div>
        </div>
      ))}
      <div
        style={{
          marginTop: 4,
          padding: "10px 12px",
          background: "rgba(16,185,129,0.08)",
          border: "1px solid rgba(16,185,129,0.2)",
          borderRadius: 10,
          fontSize: 12.5,
          color: "#047857",
          fontWeight: 600,
        }}
      >
        7 appraisals · 0 hours of your time. While you slept, Michael worked.
      </div>
    </div>
  );
}

/* ─────────── Day in the life timeline ─────────── */
const TIMELINE = [
  { time: "06:42", icon: "📊", title: "Spotted a market signal", desc: "Cottesloe prices up 4.2% MoM, flagged 12 contacts who own there." },
  { time: "08:11", icon: "🎂", title: "Wished David happy birthday", desc: "Personal note. No template. Mentioned his Bondi unit." },
  { time: "10:34", icon: "✍️", title: "Drafted 47 plain-text follow-ups", desc: "Every one referenced the contact's own property and history." },
  { time: "12:08", icon: "💬", title: "Sarah replied, interested in appraisal", desc: "Marked HOT. Handed back to John. SMS sent to John's phone." },
  { time: "14:55", icon: "📰", title: "Wrote the Surry Hills market article", desc: "On request. Published draft to your CMS for review." },
  { time: "17:22", icon: "🎯", title: "Nudged 8 incomplete analyses", desc: "Each contact got a soft 'still want me to finish that?' email." },
  { time: "21:03", icon: "🌙", title: "Quiet shift, memory updated", desc: "Indexed today's replies into per-contact relationship summaries." },
];

function DayTimeline({ isMobile }) {
  return (
    <div style={{ position: "relative", paddingLeft: isMobile ? 0 : 32 }}>
      {!isMobile && (
        <div
          style={{
            position: "absolute",
            left: 10,
            top: 8,
            bottom: 8,
            width: 2,
            background: "linear-gradient(180deg, #10b981, #3b82f6, #8b5cf6)",
            borderRadius: 2,
          }}
        />
      )}
      {TIMELINE.map((t, i) => (
        <div
          key={i}
          style={{
            position: "relative",
            display: "flex",
            gap: 14,
            marginBottom: 18,
            animation: `slideInRight 0.55s ease ${i * 0.08}s both`,
          }}
        >
          {!isMobile && (
            <div
              style={{
                position: "absolute",
                left: -28,
                top: 4,
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: "#fff",
                border: `3px solid ${BRAND_ACCENT_COLOR}`,
                boxShadow: "0 0 0 4px rgba(16,185,129,0.15)",
              }}
            />
          )}
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: BRAND_ACCENT_COLOR,
              letterSpacing: 0.5,
              minWidth: 50,
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
              paddingTop: 4,
            }}
          >
            {t.time}
          </div>
          <div
            style={{
              flex: 1,
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: "12px 14px",
              boxShadow: "0 4px 12px rgba(15,23,42,0.04)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 18 }}>{t.icon}</span>
              <div style={{ fontSize: 14, fontWeight: 800, color: BRAND_COLOR }}>{t.title}</div>
            </div>
            <div style={{ fontSize: 12.5, color: GRAY_DARK_COLOR, lineHeight: 1.5 }}>{t.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────── Comparison table ─────────── */
function ComparisonTable({ isMobile }) {
  const rows = [
    ["Personalises every email to the contact", false, true],
    ["Remembers years of past conversations", false, true],
    ["Sends from your own custom domain", false, true],
    ["Works 24/7 with zero burnout", false, true],
    ["Hands back warm leads automatically", false, true],
    ["Cost per nurtured contact", "$$$", "¢"],
    ["Time to onboard", "Weeks", "An afternoon"],
  ];
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        boxShadow: "0 20px 50px rgba(15,23,42,0.08)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1.4fr 1fr 1fr" : "2fr 1fr 1fr",
          background: BRAND_COLOR,
          color: "#fff",
        }}
      >
        <div style={{ padding: "14px 16px", fontWeight: 700, fontSize: 13 }} />
        <div style={{ padding: "14px 12px", textAlign: "center", fontWeight: 700, fontSize: 13, borderLeft: "1px solid rgba(255,255,255,0.1)" }}>
          {isMobile ? "VA" : "Virtual Assistant"}
        </div>
        <div
          style={{
            padding: "14px 12px",
            textAlign: "center",
            fontWeight: 800,
            fontSize: 13,
            background: "rgba(16,185,129,0.18)",
            borderLeft: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          AI Co-Pilot
        </div>
      </div>
      {rows.map((r, i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1.4fr 1fr 1fr" : "2fr 1fr 1fr",
            borderTop: "1px solid #f1f5f9",
            background: i % 2 ? "#fafbfc" : "#fff",
          }}
        >
          <div style={{ padding: "12px 16px", fontSize: 13, color: GRAY_DARK_COLOR, fontWeight: 600 }}>{r[0]}</div>
          <div style={{ padding: "12px", textAlign: "center", fontSize: 14, color: "#94a3b8", fontWeight: 700, borderLeft: "1px solid #f1f5f9" }}>
            {typeof r[1] === "boolean" ? (r[1] ? "✓" : "✗") : r[1]}
          </div>
          <div
            style={{
              padding: "12px",
              textAlign: "center",
              fontSize: 14,
              color: BRAND_ACCENT_COLOR,
              fontWeight: 800,
              background: "rgba(16,185,129,0.06)",
              borderLeft: "1px solid #f1f5f9",
            }}
          >
            {typeof r[2] === "boolean" ? (r[2] ? "✓" : "✗") : r[2]}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────── FAQ ─────────── */
const FAQ = [
  {
    q: "Will my contacts know it's AI?",
    a: "They'll know the agent exists as a real team member with a real photo, name, and email on your domain. The writing is plain-text, personal, and references their actual property and history, nothing about it screams 'bot.' When they reply with real intent, the conversation is handed back to you.",
  },
  {
    q: "What does my AI agent already know about each contact?",
    a: "Whatever your inbox already knows. One Gmail or Outlook export gets read once. We distill years of conversations into a short relationship summary per contact. Your raw emails stay private, only the summary briefs the AI.",
  },
  {
    q: "Do I lose control?",
    a: "Never. You choose who AI owns and who you own. Flip on Review Mode and every outbound message lands in the Needs Review tab so you can approve, edit, or dismiss it before it sends. The moment a contact warms up, they're handed back to you with full context. Your AI is a teammate, not a replacement.",
  },
  {
    q: "What's the email setup like?",
    a: "We buy and manage a custom domain for you, e.g. michael.jones@youragency.agency. No DNS, no IT, no inbox setup. You write a signature once, and we automatically inline the agent's photo on every send.",
  },
  {
    q: "Can I talk to my AI like a teammate?",
    a: "Yes. Open the chat and ask for anything, draft an article about a suburb, build an infographic, pull a shareable property link, or just ask what's happening with a contact. Same agent that handles inbound and outbound nurture.",
  },
];

function FAQItem({ item, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        overflow: "hidden",
        marginBottom: 10,
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          padding: "16px 18px",
          background: "transparent",
          border: "none",
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          fontFamily: "inherit",
          fontSize: 15,
          fontWeight: 700,
          color: BRAND_COLOR,
        }}
      >
        {item.q}
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: open ? BRAND_ACCENT_COLOR : "#f1f5f9",
            color: open ? "#fff" : GRAY_DARK_COLOR,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s",
            transform: open ? "rotate(45deg)" : "rotate(0deg)",
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          +
        </span>
      </button>
      <div
        style={{
          maxHeight: open ? 300 : 0,
          overflow: "hidden",
          transition: "max-height 0.4s ease",
        }}
      >
        <div style={{ padding: "0 18px 16px 18px", color: GRAY_DARK_COLOR, fontSize: 14, lineHeight: 1.65 }}>
          {item.a}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Lead scoring (contact list + popover) ─────────── */
const SCORED_CONTACTS = [
  { name: "Sarah Miller",  subline: "14 Maple St · Owner-occupier",   score: 92, tier: { label: "Blazing", bg: "#fee2e2", border: "#fca5a5", fg: "#7f1d1d", dot: "#dc2626" }, signals: [["Property analysed", "+30"], ["Replied to AI", "+25"], ["Repeated opens", "+15"], ["Anniversary nudge", "+10"]] },
  { name: "David Chen",    subline: "9 Bondi Rd · 1 inv. property",   score: 74, tier: { label: "Hot",     bg: "#fee2e2", border: "#fecaca", fg: "#991b1b", dot: "#ef4444" }, signals: [["Suburb searched (3x)", "+18"], ["Email click", "+8"], ["First login", "+12"]] },
  { name: "Priya Patel",   subline: "Cottesloe · vendor 2022",        score: 61, tier: { label: "Warm",    bg: "#ffedd5", border: "#fed7aa", fg: "#9a3412", dot: "#f97316" }, signals: [["Asked about prices", "+15"], ["Address searched", "+10"]] },
  { name: "Tom Reilly",    subline: "Newtown · long tail",            score: 42, tier: { label: "Mild",    bg: "#fef3c7", border: "#fde68a", fg: "#92400e", dot: "#f59e0b" }, signals: [["Email opens (4)", "+8"], ["Quiet 60+ days", "0"]] },
  { name: "Anna Petrov",   subline: "Surry Hills · investor",         score: 28, tier: { label: "Cool",    bg: "#dbeafe", border: "#bfdbfe", fg: "#1e3a8a", dot: "#3b82f6" }, signals: [["Sign-up", "+12"], ["No recent activity", "-"]] },
  { name: "Marcus Hall",   subline: "Paddington · seller 2019",       score: 12, tier: { label: "Cold",    bg: "#f1f5f9", border: "#e2e8f0", fg: "#334155", dot: "#64748b" }, signals: [["Old contact", "—"], ["No opens 6mo", "-"]] },
];

function LeadScoringDemo({ isMobile }) {
  const [focusIdx, setFocusIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setFocusIdx((i) => (i + 1) % SCORED_CONTACTS.length), 2400);
    return () => clearInterval(t);
  }, []);
  const focus = SCORED_CONTACTS[focusIdx];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1.15fr 1fr",
        gap: isMobile ? 20 : 32,
        alignItems: "stretch",
      }}
    >
      {/* Contact list */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e2e8f0",
          boxShadow: "0 20px 50px rgba(15,23,42,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            padding: "10px 14px",
            fontSize: 11,
            fontWeight: 800,
            color: "#64748b",
            letterSpacing: 0.6,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>CONTACTS · SORTED BY SCORE</span>
          <span style={{ color: BRAND_ACCENT_COLOR }}>LIVE</span>
        </div>
        {SCORED_CONTACTS.map((c, i) => {
          const active = i === focusIdx;
          return (
            <div
              key={c.name}
              onMouseEnter={() => setFocusIdx(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                borderBottom: i === SCORED_CONTACTS.length - 1 ? "none" : "1px solid #f1f5f9",
                background: active ? "rgba(16,185,129,0.05)" : "#fff",
                transition: "background 0.2s",
                cursor: "default",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, hsl(${i * 55}, 60%, 60%), hsl(${i * 55 + 40}, 60%, 50%))`,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: 12,
                  flexShrink: 0,
                }}
              >
                {c.name.split(" ").map((n) => n[0]).join("")}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{c.name}</div>
                <div style={{ fontSize: 11, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.subline}</div>
              </div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "3px 9px",
                  borderRadius: 999,
                  background: c.tier.bg,
                  border: `1px solid ${c.tier.border}`,
                  color: c.tier.fg,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  flexShrink: 0,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.tier.dot }} />
                {c.score} · {c.tier.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Popover for focused contact */}
      <div
        key={focusIdx}
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e2e8f0",
          boxShadow: "0 30px 70px rgba(15,23,42,0.15)",
          padding: 18,
          animation: "fadeUp 0.35s ease both",
          alignSelf: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: `linear-gradient(135deg, hsl(${focusIdx * 55}, 60%, 60%), hsl(${focusIdx * 55 + 40}, 60%, 50%))`,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 14,
            }}
          >
            {focus.name.split(" ").map((n) => n[0]).join("")}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{focus.name}</div>
            <div style={{ fontSize: 11, color: "#64748b" }}>{focus.subline}</div>
          </div>
          <div
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              background: focus.tier.bg,
              border: `1px solid ${focus.tier.border}`,
              color: focus.tier.fg,
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {focus.score}
          </div>
        </div>
        <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: 0.6, marginBottom: 6 }}>
          SCORE BREAKDOWN
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {focus.signals.map(([label, delta], i) => (
            <div
              key={label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                padding: "6px 10px",
                background: "#f8fafc",
                borderRadius: 6,
                color: "#0f172a",
                animation: `slideInRight 0.35s ease ${i * 0.05}s both`,
              }}
            >
              <span style={{ fontWeight: 600 }}>{label}</span>
              <span style={{ fontWeight: 800, color: delta.startsWith("+") ? "#047857" : "#64748b" }}>{delta}</span>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 12,
            padding: "8px 10px",
            background: "rgba(16,185,129,0.08)",
            border: "1px solid rgba(16,185,129,0.2)",
            borderRadius: 8,
            fontSize: 11.5,
            color: "#047857",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>🤖</span> {focus.score >= 70 ? "Hand back to you, ready to call." : focus.score >= 40 ? "AI keeps nurturing, watching for intent." : "Long-tail. Quarterly check-in only."}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Review mode / Needs Review queue ─────────── */
const REVIEW_DRAFTS = [
  {
    contact: "Sarah Miller",
    sub: "Reply to her Jan market update",
    kind: "REPLY",
    body:
      "Hi Sarah, great to hear from you. Bourke St 3-bed went for $1.72M last weekend, 14 Maple is tracking about 8.3% up. Happy to get you a proper appraisal, no obligation. ",
    confidence: 96,
  },
  {
    contact: "David Chen",
    sub: "Outbound: Bondi sales digest",
    kind: "OUTBOUND",
    body:
      "Hey David, quick one, 9 Bondi Rd's block has had two recent sales worth flagging for your investment outlook. Want me to send the comp sheet? ",
    confidence: 88,
  },
  {
    contact: "Priya Patel",
    sub: "Reply: price question",
    kind: "REPLY",
    body:
      "Hi Priya, Cottesloe medians lifted 4.2% MoM. I've pulled a quick comp set for your block, attached. Let me know if you want me to walk through it. ",
    confidence: 92,
  },
];

function NeedsReviewDemo({ isMobile }) {
  const [reviewMode, setReviewMode] = useState(true);
  const [drafts, setDrafts] = useState(REVIEW_DRAFTS);
  const [activeIdx, setActiveIdx] = useState(0);

  // Cycle which draft is highlighted while idle.
  useEffect(() => {
    const t = setInterval(() => {
      setActiveIdx((i) => (drafts.length === 0 ? 0 : (i + 1) % drafts.length));
    }, 3200);
    return () => clearInterval(t);
  }, [drafts.length]);

  const active = drafts[activeIdx] || drafts[0];

  return (
    <div
      style={{
        background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)",
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,0.08)",
        padding: isMobile ? "1.25rem" : "1.75rem",
        boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
      }}
    >
      {/* Review-mode toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "12px 14px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: BRAND_ACCENT_COLOR, letterSpacing: 0.6 }}>
            REVIEW MODE
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.78)", marginTop: 2 }}>
            {reviewMode
              ? "Every outbound message queues for your approval first."
              : "AI sends on its own. You can flip review mode back on any time."}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setReviewMode((v) => !v)}
          style={{
            position: "relative",
            width: 52,
            height: 28,
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            background: reviewMode ? BRAND_ACCENT_COLOR : "rgba(255,255,255,0.15)",
            transition: "background 0.2s",
            flexShrink: 0,
          }}
          aria-label="Toggle review mode"
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: reviewMode ? 27 : 3,
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              transition: "left 0.2s",
            }}
          />
        </button>
      </div>

      {/* Browser-style frame */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
        }}
      >
        {/* Tabs */}
        <div
          style={{
            display: "flex",
            background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            padding: "8px 10px 0",
            gap: 6,
          }}
        >
          {[
            { k: "review", label: `Needs review`, count: drafts.length, active: true },
            { k: "all",    label: "All conversations", count: null,        active: false },
            { k: "inbox",  label: "Inbox",             count: null,        active: false },
          ].map((tab) => (
            <div
              key={tab.k}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 700,
                color: tab.active ? "#0f172a" : "#64748b",
                background: tab.active ? "#fff" : "transparent",
                borderRadius: "8px 8px 0 0",
                border: tab.active ? "1px solid #e2e8f0" : "1px solid transparent",
                borderBottom: tab.active ? "1px solid #fff" : "none",
                position: "relative",
                top: 1,
              }}
            >
              {tab.label}
              {tab.count !== null && (
                <span
                  style={{
                    background: tab.active ? "#fef3c7" : "#e2e8f0",
                    color: tab.active ? "#92400e" : "#475569",
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "1px 6px",
                    borderRadius: 999,
                  }}
                >
                  {tab.count}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Two-pane: queue + draft preview */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "0.9fr 1.1fr",
            minHeight: 320,
          }}
        >
          {/* Queue */}
          <div style={{ borderRight: isMobile ? "none" : "1px solid #e2e8f0", borderBottom: isMobile ? "1px solid #e2e8f0" : "none" }}>
            {drafts.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: "#64748b", fontSize: 13 }}>
                ✅ Inbox clear. Nothing waiting on you.
              </div>
            ) : (
              drafts.map((d, i) => {
                const isActive = i === activeIdx;
                return (
                  <div
                    key={d.contact + i}
                    onMouseEnter={() => setActiveIdx(i)}
                    style={{
                      padding: "12px 14px",
                      borderBottom: i === drafts.length - 1 ? "none" : "1px solid #f1f5f9",
                      background: isActive ? "rgba(16,185,129,0.06)" : "#fff",
                      borderLeft: `3px solid ${isActive ? BRAND_ACCENT_COLOR : "transparent"}`,
                      cursor: "default",
                      transition: "background 0.2s",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>{d.contact}</span>
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 800,
                          padding: "2px 6px",
                          borderRadius: 999,
                          letterSpacing: 0.5,
                          background: d.kind === "REPLY" ? "rgba(16,185,129,0.12)" : "rgba(59,130,246,0.12)",
                          color: d.kind === "REPLY" ? BRAND_ACCENT_COLOR : BRAND_BLUE,
                        }}
                      >
                        {d.kind}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{d.sub}</div>
                  </div>
                );
              })
            )}
          </div>

          {/* Draft preview */}
          {active && (
            <div key={activeIdx} style={{ padding: 16, animation: "fadeIn 0.3s ease both" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>Draft for {active.contact}</div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "rgba(16,185,129,0.12)",
                    color: BRAND_ACCENT_COLOR,
                    letterSpacing: 0.5,
                  }}
                >
                  AI CONFIDENCE {active.confidence}%
                </div>
              </div>
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 12.5,
                  color: "#0f172a",
                  lineHeight: 1.55,
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                  whiteSpace: "pre-wrap",
                  minHeight: 130,
                }}
              >
                {active.body}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => {
                    setDrafts((d) => d.filter((_, i) => i !== activeIdx));
                    setActiveIdx(0);
                  }}
                  style={{
                    flex: 1,
                    minWidth: 110,
                    background: BRAND_ACCENT_COLOR,
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 12.5,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  ✓ Approve & send
                </button>
                <button
                  type="button"
                  style={{
                    background: "#fff",
                    color: "#475569",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDrafts((d) => d.filter((_, i) => i !== activeIdx));
                    setActiveIdx(0);
                  }}
                  style={{
                    background: "#fff",
                    color: "#dc2626",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Main ─────────── */
export default function AICoPilot() {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  const [showContact, setShowContact] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", color: GRAY_DARK_COLOR, background: "#fff" }}>
      <GlobalAnimations />
      <Navbar onSignIn={() => setShowSignIn(true)} />

      {/* ───── HERO ───── */}
      <header
        style={{
          background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 50%,#134e4a 100%)",
          padding: isMobile ? "4rem 1.25rem 5rem" : "6rem 2rem 7rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "-20%",
            left: "60%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(16,185,129,0.18), transparent 60%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1.05fr",
            gap: isMobile ? "2.5rem" : "4rem",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(16,185,129,0.15)",
                border: "1px solid rgba(16,185,129,0.3)",
                color: BRAND_ACCENT_COLOR,
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 999,
                padding: "5px 14px",
                marginBottom: 20,
                letterSpacing: 0.3,
                animation: "badgeIn 0.5s ease both",
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: "50%", animation: "statusBlink 1.6s ease infinite" }} />
              AI CO-PILOT · YOUR 24/7 TEAMMATE
            </div>
            <h1
              style={{
                fontSize: isMobile ? "2.1rem" : "3.3rem",
                fontWeight: 900,
                lineHeight: 1.08,
                color: "#fff",
                letterSpacing: "-0.02em",
                marginBottom: 18,
              }}
            >
              Hire an AI agent with a{" "}
              <span style={{ position: "relative", whiteSpace: "nowrap" }}>
                face,
                <svg
                  style={{ position: "absolute", left: 0, right: 0, bottom: -6, width: "100%", height: 8 }}
                  viewBox="0 0 100 8"
                  preserveAspectRatio="none"
                >
                  <path d="M2 5 Q 50 0 98 5" stroke={BRAND_ACCENT_COLOR} strokeWidth="2.5" fill="none" strokeLinecap="round" />
                </svg>
              </span>{" "}
              a{" "}
              <span
                style={{
                  background: "linear-gradient(90deg,#10b981,#3b82f6)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                name
              </span>
              , and a real inbox.
            </h1>
            <p
              style={{
                fontSize: isMobile ? "1rem" : "1.18rem",
                color: "rgba(255,255,255,0.78)",
                marginBottom: 28,
                lineHeight: 1.65,
                maxWidth: 520,
              }}
            >
              Your AI nurtures your database 24/7. It drafts plain-text emails, SMS follow-ups, and call notes
              that reference every contact's property and history. When someone gets warm, they're handed back
              to you, already in context.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 28 }}>
              <button
                onClick={() => setShowSignIn(true)}
                style={{
                  border: "none",
                  borderRadius: 10,
                  padding: "14px 28px",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: BRAND_ACCENT_COLOR,
                  color: "#fff",
                  boxShadow: "0 8px 24px rgba(16,185,129,0.4)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 12px 30px rgba(16,185,129,0.55)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(16,185,129,0.4)";
                }}
              >
                Hire your AI agent →
              </button>
              <button
                onClick={() => setShowContact(true)}
                style={{
                  border: "1px solid rgba(255,255,255,0.25)",
                  borderRadius: 10,
                  padding: "13px 24px",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "transparent",
                  color: "#fff",
                }}
              >
                Book a walkthrough
              </button>
            </div>

            {/* Stat row */}
            <div style={{ display: "flex", gap: isMobile ? 16 : 28, flexWrap: "wrap" }}>
              {[
                ["24/7", "Never sleeps"],
                ["Email · SMS · Calls", "All drafted for you"],
                ["1-click", "Hand back"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div style={{ color: BRAND_ACCENT_COLOR, fontSize: 18, fontWeight: 800 }}>{n}</div>
                  <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 600, letterSpacing: 0.3 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ position: "relative" }}>
            <HeroEmailDraft isMobile={isMobile} />

            {/* Floating notif */}
            {!isMobile && <div
              style={{
                position: "absolute",
                top: -16,
                right: -16,
                background: "#fff",
                borderRadius: 12,
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                boxShadow: "0 16px 40px rgba(0,0,0,0.25)",
                animation: "floatY 4s ease-in-out infinite, slideInDown 0.6s ease 0.4s both",
                zIndex: 5,
                maxWidth: 240,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "rgba(239,68,68,0.12)",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  flexShrink: 0,
                }}
              >
                🔥
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>Sarah replied</div>
                <div style={{ fontSize: 10.5, color: "#64748b" }}>Handed back · warm lead</div>
              </div>
            </div>}

            {/* Bottom-left badge */}
            {!isMobile && <div
              style={{
                position: "absolute",
                bottom: -14,
                left: -16,
                background: "#0f172a",
                color: "#fff",
                borderRadius: 12,
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                boxShadow: "0 16px 40px rgba(0,0,0,0.4)",
                animation: "floatY 4.5s ease-in-out 0.5s infinite, slideInDown 0.6s ease 0.6s both",
                border: "1px solid rgba(255,255,255,0.1)",
                zIndex: 5,
              }}
            >
              <img src={AI_AGENT.photo} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 800 }}>{AI_AGENT.full_name}</div>
                <div style={{ fontSize: 10, color: BRAND_ACCENT_COLOR, fontWeight: 700 }}>online · drafted 47 today</div>
              </div>
            </div>}
          </div>
        </div>
      </header>

      {/* ───── ANATOMY ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div
              style={{
                display: "inline-block",
                background: "rgba(16,185,129,0.1)",
                color: BRAND_ACCENT_COLOR,
                fontSize: 12,
                fontWeight: 800,
                padding: "5px 14px",
                borderRadius: 999,
                letterSpacing: 1,
                marginBottom: 14,
              }}
            >
              ANATOMY OF AN AI AGENT
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.4rem",
                fontWeight: 800,
                color: BRAND_COLOR,
                letterSpacing: "-0.02em",
                marginBottom: 12,
              }}
            >
              Not a chatbot. A teammate.
            </h2>
            <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, maxWidth: 620, margin: "0 auto", lineHeight: 1.6 }}>
              Real face. Real name. Real inbox on your domain. Real memory of every conversation.
            </p>
          </div>
          <AgentAnatomy isMobile={isMobile} />
        </div>
      </section>

      {/* ───── 5-STEP WALKTHROUGH ───── */}
      <section
        style={{
          padding: isMobile ? "4rem 1.25rem" : "6rem 2rem",
          background: "#f8fafc",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div
              style={{
                display: "inline-block",
                background: "rgba(59,130,246,0.1)",
                color: BRAND_BLUE,
                fontSize: 12,
                fontWeight: 800,
                padding: "5px 14px",
                borderRadius: 999,
                letterSpacing: 1,
                marginBottom: 14,
              }}
            >
              FIVE STEPS · ONE AFTERNOON
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.4rem",
                fontWeight: 800,
                color: BRAND_COLOR,
                letterSpacing: "-0.02em",
                marginBottom: 12,
              }}
            >
              From hire to first reply.
            </h2>
            <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, maxWidth: 620, margin: "0 auto", lineHeight: 1.6 }}>
              Set up the persona, import inbox history, assign ownership, brief the AI, and watch replies arrive.
            </p>
          </div>
          <WalkthroughStepper isMobile={isMobile} />
        </div>
      </section>

      {/* ───── MEMORY ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div
              style={{
                display: "inline-block",
                background: "rgba(16,185,129,0.1)",
                color: BRAND_ACCENT_COLOR,
                fontSize: 12,
                fontWeight: 800,
                padding: "5px 14px",
                borderRadius: 999,
                letterSpacing: 1,
                marginBottom: 14,
              }}
            >
              AGENT MEMORY
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.4rem",
                fontWeight: 800,
                color: BRAND_COLOR,
                letterSpacing: "-0.02em",
                marginBottom: 12,
              }}
            >
              Years of context. One export.
            </h2>
            <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, maxWidth: 640, margin: "0 auto", lineHeight: 1.6 }}>
              Drag a Gmail or Outlook export in. Your AI reads every conversation you've ever had with each
              contact and walks into the next one already knowing where the relationship is up to.
            </p>
          </div>
          <MemoryDemo isMobile={isMobile} />
        </div>
      </section>

      {/* ───── TRIGGERS ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#f8fafc" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? 32 : 48,
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-block",
                  background: "rgba(245,158,11,0.12)",
                  color: "#d97706",
                  fontSize: 12,
                  fontWeight: 800,
                  padding: "5px 14px",
                  borderRadius: 999,
                  letterSpacing: 1,
                  marginBottom: 14,
                }}
              >
                SMART TRIGGERS
              </div>
              <h2
                style={{
                  fontSize: isMobile ? "1.75rem" : "2.4rem",
                  fontWeight: 800,
                  color: BRAND_COLOR,
                  letterSpacing: "-0.02em",
                  marginBottom: 16,
                  lineHeight: 1.2,
                }}
              >
                The right message, the right moment.
              </h2>
              <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, lineHeight: 1.7, marginBottom: 20 }}>
                Your AI quietly watches for the moments that matter, anniversaries, birthdays, repeated
                opens, first logins, long silences, and reaches out with something useful. Every send
                is written from scratch for that one contact.
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  "Plain-text. Always.",
                  "Personal. Always.",
                  "Trigger-aware. Always.",
                  "Reviewable. Always (if you want).",
                ].map((b) => (
                  <li key={b} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 600, color: GRAY_DARK_COLOR }}>
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: "rgba(16,185,129,0.15)",
                        color: BRAND_ACCENT_COLOR,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: 12,
                      }}
                    >
                      ✓
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <TriggerRadial isMobile={isMobile} />
            </div>
          </div>
        </div>
      </section>

      {/* ───── LEAD SCORING ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div
              style={{
                display: "inline-block",
                background: "rgba(239,68,68,0.1)",
                color: "#dc2626",
                fontSize: 12,
                fontWeight: 800,
                padding: "5px 14px",
                borderRadius: 999,
                letterSpacing: 1,
                marginBottom: 14,
              }}
            >
              LEAD SCORING
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.4rem",
                fontWeight: 800,
                color: BRAND_COLOR,
                letterSpacing: "-0.02em",
                marginBottom: 12,
              }}
            >
              {AI_AGENT.first_name} scores every contact, every day.
            </h2>
            <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, maxWidth: 660, margin: "0 auto", lineHeight: 1.6 }}>
              Property analyses, logins, suburb searches, replies, opens, agent notes, every signal flows into a
              live score. Your list re-sorts itself so the hottest contacts are always on top.
            </p>
          </div>
          <LeadScoringDemo isMobile={isMobile} />
        </div>
      </section>

      {/* ───── CHAT ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? 32 : 48,
              alignItems: "center",
            }}
          >
            <div style={{ order: isMobile ? 1 : 0 }}>
              <TeammateChat isMobile={isMobile} />
            </div>
            <div>
              <div
                style={{
                  display: "inline-block",
                  background: "rgba(59,130,246,0.1)",
                  color: BRAND_BLUE,
                  fontSize: 12,
                  fontWeight: 800,
                  padding: "5px 14px",
                  borderRadius: 999,
                  letterSpacing: 1,
                  marginBottom: 14,
                }}
              >
                CHAT WITH YOUR AI
              </div>
              <h2
                style={{
                  fontSize: isMobile ? "1.75rem" : "2.4rem",
                  fontWeight: 800,
                  color: BRAND_COLOR,
                  letterSpacing: "-0.02em",
                  marginBottom: 16,
                  lineHeight: 1.2,
                }}
              >
                Talk to {AI_AGENT.first_name} like a real teammate.
              </h2>
              <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, lineHeight: 1.7, marginBottom: 16 }}>
                Ask questions about your contacts. Get articles written. Build infographics on demand.
                Pull a shareable property link. Same agent that handles your inbound and outbound.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {[
                  '"Article about Surry Hills market"',
                  '"Infographic of Bondi sales trends"',
                  '"Draft a social schedule"',
                  '"What\'s the status with David?"',
                ].map((p) => (
                  <span
                    key={p}
                    style={{
                      background: "#f1f5f9",
                      color: BRAND_COLOR,
                      fontSize: 12.5,
                      fontWeight: 600,
                      padding: "6px 12px",
                      borderRadius: 999,
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── DAY IN THE LIFE ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#f8fafc" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div
              style={{
                display: "inline-block",
                background: "rgba(139,92,246,0.12)",
                color: "#7c3aed",
                fontSize: 12,
                fontWeight: 800,
                padding: "5px 14px",
                borderRadius: 999,
                letterSpacing: 1,
                marginBottom: 14,
              }}
            >
              A DAY IN THE LIFE
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.4rem",
                fontWeight: 800,
                color: BRAND_COLOR,
                letterSpacing: "-0.02em",
                marginBottom: 12,
              }}
            >
              While you slept, {AI_AGENT.first_name} worked.
            </h2>
            <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, lineHeight: 1.6 }}>
              A typical Tuesday in your AI agent's inbox.
            </p>
          </div>
          <DayTimeline isMobile={isMobile} />
        </div>
      </section>

      {/* ───── RESULTS ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1.2fr",
              gap: isMobile ? 32 : 56,
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-block",
                  background: "rgba(16,185,129,0.1)",
                  color: BRAND_ACCENT_COLOR,
                  fontSize: 12,
                  fontWeight: 800,
                  padding: "5px 14px",
                  borderRadius: 999,
                  letterSpacing: 1,
                  marginBottom: 14,
                }}
              >
                LAST 30 DAYS
              </div>
              <h2
                style={{
                  fontSize: isMobile ? "1.75rem" : "2.4rem",
                  fontWeight: 800,
                  color: BRAND_COLOR,
                  letterSpacing: "-0.02em",
                  marginBottom: 16,
                  lineHeight: 1.2,
                }}
              >
                From cold list to booked appraisals, on autopilot.
              </h2>
              <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, lineHeight: 1.7 }}>
                Watch the funnel work itself out. Your AI works the wide end. You work the warm end.
                Nobody slips through the cracks.
              </p>
            </div>
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                border: "1px solid #e2e8f0",
                boxShadow: "0 30px 60px rgba(15,23,42,0.08)",
                padding: 24,
              }}
            >
              <ResultsChart isMobile={isMobile} />
            </div>
          </div>
        </div>
      </section>

      {/* ───── COMPARISON ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#f8fafc" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.4rem",
                fontWeight: 800,
                color: BRAND_COLOR,
                letterSpacing: "-0.02em",
                marginBottom: 12,
              }}
            >
              VA vs AI Co-Pilot
            </h2>
            <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, maxWidth: 580, margin: "0 auto", lineHeight: 1.6 }}>
              A virtual assistant works hours. {AI_AGENT.first_name} works always.
            </p>
          </div>
          <ComparisonTable isMobile={isMobile} />
        </div>
      </section>

      {/* ───── STAY IN CONTROL ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div
              style={{
                display: "inline-block",
                background: "rgba(59,130,246,0.1)",
                color: BRAND_BLUE,
                fontSize: 12,
                fontWeight: 800,
                padding: "5px 14px",
                borderRadius: 999,
                letterSpacing: 1,
                marginBottom: 14,
              }}
            >
              STAY IN CONTROL
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.4rem",
                fontWeight: 800,
                color: BRAND_COLOR,
                letterSpacing: "-0.02em",
                marginBottom: 12,
              }}
            >
              Review every outbound message. Or don't.
            </h2>
            <p style={{ fontSize: 16, color: GRAY_DARK_COLOR, maxWidth: 660, margin: "0 auto", lineHeight: 1.6 }}>
              Flip on Review Mode and every reply or outbound draft queues in your Needs Review tab. Approve,
              edit, or dismiss with one click. Flip it off and your AI sends on its own. You're never locked in
              either way.
            </p>
          </div>
          <NeedsReviewDemo isMobile={isMobile} />
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section style={{ padding: isMobile ? "4rem 1.25rem" : "6rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.4rem",
                fontWeight: 800,
                color: BRAND_COLOR,
                letterSpacing: "-0.02em",
              }}
            >
              The real questions
            </h2>
          </div>
          {FAQ.map((f, i) => (
            <FAQItem key={i} item={f} idx={i} />
          ))}
        </div>
      </section>

      {/* ───── CTA ───── */}
      <section
        style={{
          background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 50%,#134e4a 100%)",
          padding: isMobile ? "4rem 1.25rem" : "6rem 2rem",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translateX(-50%)",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(16,185,129,0.15), transparent 65%)",
            pointerEvents: "none",
          }}
        />
        <div style={{ maxWidth: 700, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div style={{ position: "relative", display: "inline-block", marginBottom: 20 }}>
            <span
              style={{
                position: "absolute",
                inset: -10,
                borderRadius: "50%",
                animation: "ringPulse 2s ease-out infinite",
                background: "rgba(16,185,129,0.5)",
              }}
            />
            <img
              src={AI_AGENT.photo}
              alt=""
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                objectFit: "cover",
                border: "3px solid rgba(255,255,255,0.2)",
                position: "relative",
                zIndex: 1,
              }}
            />
          </div>
          <h2
            style={{
              fontSize: isMobile ? "2rem" : "2.8rem",
              fontWeight: 900,
              color: "#fff",
              marginBottom: 16,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
            }}
          >
            Your AI teammate is ready to start.
          </h2>
          <p style={{ color: "rgba(255,255,255,0.7)", marginBottom: 32, fontSize: isMobile ? 15 : 17, lineHeight: 1.6 }}>
            Set up the persona, plug in inbox history, choose who they own, and your AI agent goes to
            work nurturing your database 24/7. You stay in control. They handle the long tail.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <button
              onClick={() => setShowSignIn(true)}
              style={{
                border: "none",
                borderRadius: 10,
                padding: "15px 32px",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                background: BRAND_ACCENT_COLOR,
                color: "#fff",
                boxShadow: "0 8px 24px rgba(16,185,129,0.4)",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 30px rgba(16,185,129,0.55)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(16,185,129,0.4)";
              }}
            >
              Hire your AI agent →
            </button>
            <button
              onClick={() => setShowContact(true)}
              style={{
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 10,
                padding: "14px 28px",
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
                background: "transparent",
                color: "#fff",
              }}
            >
              Talk to us first
            </button>
          </div>
        </div>
      </section>

      {showSignIn && <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />}
      {showContact && <ContactVendorleadsModal show={showContact} onHide={() => setShowContact(false)} />}
    </div>
  );
}
